<template>
    <div class="relative" :class="{'rounded-md': rounded}">
        <div v-if="label" class="mb-2">{{ label }}</div>
        <div class="flex justify-between items-center" @click="openDropdown" :class="[disable ? 'text-light-gray' : 'cursor-pointer']">
            <span class="flex items-center w-full">
                <slot name="title" :open="open" :title="activeTitle" :num-selected="activeValue.length">
                    {{ activeTitle }}
                </slot>
                <icon v-if="showIcon" name="chevron-down"
                      class="relative top-px w-3 h-3 flex-shrink-0 transition-transform transform origin-center fill-current ml-1.5"
                      :class="{'rotate-180': open}"
                />
            </span>
        </div>
        <collapse-transition>
            <div v-show="open"
                 class="absolute top-full z-30 min-w-max w-full bg-white overflow-hidden rounded-md border border-light-gray mt-1.5"
                 :class="[
                     alignment === 'left' ? 'left-0' : 'right-0',
                     rounded ? 'rounded-md' : '',
                     additionalDropdownClasses
                 ]">
                <div class="overflow-y-auto overflow-x-hidden p-3"
                     :class="[
                         shortHeight ? 'max-h-48' : 'max-h-96'
                     ]">
                    <slot :active-title="activeTitle" />
                </div>
            </div>
        </collapse-transition>
        <slot name="selection" :value="activeValue" />
    </div>
</template>

<script lang="ts">
import Vue from 'vue';
import { Component, Prop, Provide } from 'vue-property-decorator';
import { CollapseTransition } from '@ivanv/vue-collapse-transition';
import Icon from './Icon.vue';
import DropdownItem from './DropdownItem.vue';

@Component({
    components: { Icon, CollapseTransition }
})
export default class Dropdown extends Vue {
    @Provide('register') childRegistration = this.registerItem;
    @Prop() label: string;
    @Prop({ default: 'left' }) alignment: 'left' | 'right';
    @Prop({ default: true, type: Boolean }) shortHeight: boolean;
    @Prop({ default: false, type: Boolean }) showIcon: boolean;
    @Prop({ default: false, type: Boolean }) disable: boolean;
    @Prop({ default: true, type: Boolean }) clearable: boolean;
    @Prop({ default: false, type: Boolean }) rounded: boolean;
    @Prop({ default: -1, type: Number }) defaultIndex: number;
    @Prop({ default: '' }) additionalDropdownClasses: string;
    @Prop({ default: '' }) title!: string;
    @Prop({ default: false, type: Boolean }) multiselect: boolean;

    open = false;
    activeTitle = this.title;
    activeValue: string | string[] = '';
    items: DropdownItem[] = [];

    created(): void {
        // Initialize activeValue based on multiselect mode
        if (this.multiselect) {
            this.activeValue = [];
            this.activeTitle = this.label;
        }
    }

    mounted(): void {
        document.addEventListener('click', this.onOutsideClick);
        // preselect if applicable
        if (this.defaultIndex >= 0 && this.items.length > this.defaultIndex) {
            this.activeTitle = this.items[this.defaultIndex].label;
            this.activeValue = this.items[this.defaultIndex].value;
        }
    }

    beforeDestroy(): void {
        document.removeEventListener('click', this.onOutsideClick);
    }

    // open/close dropdown
    openDropdown(): void {
        if (!this.disable) {
            this.open = !this.open;
        }
    }

    // called by child items
    registerItem(item: DropdownItem): void {
        this.items.push(item);
        item.$on('click', ({ label, value }) => {
            if (this.multiselect) {
                // Multi-select mode: always keep label as activeTitle
                this.activeTitle = this.label;
                const valueArray = this.activeValue as string[];
                const index = valueArray.indexOf(value);
                if (index >= 0) {
                    // Remove value if already selected
                    valueArray.splice(index, 1);
                } else {
                    // Add value if not selected
                    valueArray.push(value);
                }
                this.$emit('select', { label, value, selected: valueArray });
                this.activeValue = this.items.filter(x => x.active).map(x => x.value);
                // Keep dropdown open in multiselect mode
            } else {
                // Single-select mode (original behavior)
                if (this.clearable) {
                    this.activeTitle = this.activeTitle === label ? this.title : label;
                    this.activeValue = this.activeValue === value ? '' : value;
                } else {
                    this.activeTitle = label;
                    this.activeValue = value;
                }
                this.$emit('select', { label, value });
                this.open = false;
            }
        });
        item.$on('deactivate', this.updateActiveItems);
    }

    onOutsideClick(evt): void {
        let targetElement = evt.target;

        do {
            if (targetElement === this.$el) {
                return;
            }
            targetElement = targetElement.parentNode;
        } while (targetElement);
        this.open = false;
    }

    updateActiveItems() {
        if (this.multiselect) {
            this.activeValue = this.items.filter(x => x.active).map(x => x.value);
        }
    }
}
</script>
